import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import moment from 'moment';
import { 
  FaDollarSign, 
  FaFileInvoiceDollar, 
  FaChartLine, 
  FaExclamationCircle,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaHospital
} from 'react-icons/fa';

interface BillingStats {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  invoiceCount: number;
  paidCount: number;
  overdueCount: number;
}

interface Invoice {
  id: number;
  invoice_number: string;
  patient_name: string;
  date: string;
  amount: number;
  paid: number;
  balance: number;
  status: 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'cancelled';
  due_date: string;
}

interface ClaimSummary {
  total: number;
  pending: number;
  paid: number;
  rejected: number;
  pendingAmount: number;
  paidAmount: number;
}

const BillingDashboard: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState<BillingStats>({
    totalInvoiced: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    invoiceCount: 0,
    paidCount: 0,
    overdueCount: 0
  });
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [claimSummary, setClaimSummary] = useState<ClaimSummary>({
    total: 0,
    pending: 0,
    paid: 0,
    rejected: 0,
    pendingAmount: 0,
    paidAmount: 0
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: moment().startOf('month').format('YYYY-MM-DD'),
    to: moment().endOf('month').format('YYYY-MM-DD')
  });

  useEffect(() => {
    loadDashboardData();
  }, [dateRange, token]);

  const loadDashboardData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Load invoices
      const invoiceResponse = await apiCall(
        `/billing/invoices/?from=${dateRange.from}&to=${dateRange.to}`,
        { method: 'GET' },
        token
      );
      const invoiceData = await invoiceResponse.json();
      const invoices = invoiceData.results || invoiceData || [];
      setRecentInvoices(invoices.slice(0, 10));
      
      // Calculate stats
      const totalInvoiced = invoices.reduce((sum: number, inv: Invoice) => sum + inv.amount, 0);
      const totalPaid = invoices.reduce((sum: number, inv: Invoice) => sum + inv.paid, 0);
      const totalOutstanding = totalInvoiced - totalPaid;
      const invoiceCount = invoices.length;
      const paidCount = invoices.filter((inv: Invoice) => inv.status === 'paid').length;
      const overdueCount = invoices.filter((inv: Invoice) => inv.status === 'overdue').length;
      
      setStats({
        totalInvoiced,
        totalPaid,
        totalOutstanding,
        invoiceCount,
        paidCount,
        overdueCount
      });

      // Load claim summary
      const claimResponse = await apiCall(
        `/billing/eclipse-claims/?from=${dateRange.from}&to=${dateRange.to}`,
        { method: 'GET' },
        token
      );
      const claimData = await claimResponse.json();
      const claims = claimData.results || claimData || [];
      
      setClaimSummary({
        total: claims.length,
        pending: claims.filter((c: any) => c.status === 'pending').length,
        paid: claims.filter((c: any) => c.status === 'paid').length,
        rejected: claims.filter((c: any) => c.status === 'rejected').length,
        pendingAmount: claims.filter((c: any) => c.status === 'pending').reduce((sum: number, c: any) => sum + (c.amount || 0), 0),
        paidAmount: claims.filter((c: any) => c.status === 'paid').reduce((sum: number, c: any) => sum + (c.amount || 0), 0)
      });
    } catch (error) {
      console.error('Error loading billing data:', error);
      // Mock data
      const mockInvoices: Invoice[] = [
        {
          id: 1,
          invoice_number: 'INV-2025-001',
          patient_name: 'John Smith',
          date: '2025-10-25',
          amount: 450.00,
          paid: 450.00,
          balance: 0,
          status: 'paid',
          due_date: '2025-11-08'
        },
        {
          id: 2,
          invoice_number: 'INV-2025-002',
          patient_name: 'Mary Johnson',
          date: '2025-10-26',
          amount: 320.00,
          paid: 0,
          balance: 320.00,
          status: 'sent',
          due_date: '2025-11-09'
        },
        {
          id: 3,
          invoice_number: 'INV-2025-003',
          patient_name: 'Robert Brown',
          date: '2025-10-20',
          amount: 580.00,
          paid: 0,
          balance: 580.00,
          status: 'overdue',
          due_date: '2025-11-03'
        }
      ];
      setRecentInvoices(mockInvoices);
      setStats({
        totalInvoiced: 1350.00,
        totalPaid: 450.00,
        totalOutstanding: 900.00,
        invoiceCount: 3,
        paidCount: 1,
        overdueCount: 1
      });
      setClaimSummary({
        total: 15,
        pending: 8,
        paid: 6,
        rejected: 1,
        pendingAmount: 2400.00,
        paidAmount: 1800.00
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      draft: 'badge-ghost',
      sent: 'badge-info',
      paid: 'badge-success',
      partial: 'badge-warning',
      overdue: 'badge-error',
      cancelled: 'badge-error'
    };
    return `badge ${statusMap[status as keyof typeof statusMap] || 'badge-ghost'}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
          <FaDollarSign className="text-primary" />
          Billing Dashboard
        </h1>
        
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium whitespace-nowrap">Date From</span>
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="input input-bordered"
          />
          <span className="text-sm font-medium whitespace-nowrap">Date To</span>
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            className="input input-bordered"
          />
        </div>
      </div>

      {/* Revenue Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stats shadow bg-gradient-to-br from-primary to-primary-focus text-primary-content">
          <div className="stat">
            <div className="stat-figure text-primary-content opacity-70">
              <FaChartLine className="text-4xl" />
            </div>
            <div className="stat-title text-primary-content opacity-80">Total Invoiced</div>
            <div className="stat-value">{formatCurrency(stats.totalInvoiced)}</div>
            <div className="stat-desc text-primary-content opacity-70">{stats.invoiceCount} invoices</div>
          </div>
        </div>

        <div className="stats shadow bg-gradient-to-br from-success to-success-focus text-success-content">
          <div className="stat">
            <div className="stat-figure text-success-content opacity-70">
              <FaCheckCircle className="text-4xl" />
            </div>
            <div className="stat-title text-success-content opacity-80">Total Paid</div>
            <div className="stat-value">{formatCurrency(stats.totalPaid)}</div>
            <div className="stat-desc text-success-content opacity-70">{stats.paidCount} paid</div>
          </div>
        </div>

        <div className="stats shadow bg-gradient-to-br from-warning to-warning-focus text-warning-content">
          <div className="stat">
            <div className="stat-figure text-warning-content opacity-70">
              <FaClock className="text-4xl" />
            </div>
            <div className="stat-title text-warning-content opacity-80">Outstanding</div>
            <div className="stat-value">{formatCurrency(stats.totalOutstanding)}</div>
            <div className="stat-desc text-warning-content opacity-70">{stats.overdueCount} overdue</div>
          </div>
        </div>
      </div>

      {/* Medicare Claims Summary */}
      <div className="card bg-base-100 shadow-xl mb-6">
        <div className="card-body">
          <h2 className="card-title mb-4">
            <FaHospital className="text-primary" />
            Medicare Claims Status
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="stat bg-base-200 rounded-lg">
              <div className="stat-title">Total Claims</div>
              <div className="stat-value text-primary">{claimSummary.total}</div>
            </div>
            
            <div className="stat bg-base-200 rounded-lg">
              <div className="stat-title">Pending</div>
              <div className="stat-value text-warning">{claimSummary.pending}</div>
              <div className="stat-desc">{formatCurrency(claimSummary.pendingAmount)}</div>
            </div>
            
            <div className="stat bg-base-200 rounded-lg">
              <div className="stat-title">Paid</div>
              <div className="stat-value text-success">{claimSummary.paid}</div>
              <div className="stat-desc">{formatCurrency(claimSummary.paidAmount)}</div>
            </div>
            
            <div className="stat bg-base-200 rounded-lg">
              <div className="stat-title">Rejected</div>
              <div className="stat-value text-error">{claimSummary.rejected}</div>
            </div>
          </div>

          <div className="card-actions justify-end mt-4">
            <button 
              onClick={() => navigate('/billing/eclipse-claims')}
              className="btn btn-primary"
            >
              View All Claims
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4 mb-6">
        <button 
          onClick={() => navigate('/billing/invoices')}
          className="btn btn-primary"
        >
          <FaFileInvoiceDollar className="mr-2" />
          View All Invoices
        </button>
        <button 
          onClick={() => navigate('/billing/templates')}
          className="btn btn-secondary"
        >
          <FaChartLine className="mr-2" />
          Billing Templates
        </button>
      </div>

      {/* Recent Invoices */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title mb-4">
            <FaFileInvoiceDollar className="text-primary" />
            Recent Invoices
          </h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Patient</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInvoices.map(invoice => (
                    <tr key={invoice.id} className="hover">
                      <td className="font-mono font-bold">{invoice.invoice_number}</td>
                      <td>{invoice.patient_name}</td>
                      <td>{moment(invoice.date).format('DD/MM/YYYY')}</td>
                      <td className="font-semibold">{formatCurrency(invoice.amount)}</td>
                      <td className="text-success">{formatCurrency(invoice.paid)}</td>
                      <td className={invoice.balance > 0 ? 'text-warning font-semibold' : ''}>
                        {formatCurrency(invoice.balance)}
                      </td>
                      <td>
                        {moment(invoice.due_date).format('DD/MM/YYYY')}
                        {invoice.status === 'overdue' && (
                          <div className="text-xs text-error">
                            {moment(invoice.due_date).fromNow()}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={getStatusBadge(invoice.status)}>
                          {invoice.status}
                        </span>
                      </td>
                      <td>
                        <button 
                          onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                          className="btn btn-xs btn-ghost"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {stats.overdueCount > 0 && (
        <div className="alert alert-warning mt-6 shadow-lg">
          <FaExclamationCircle />
          <div>
            <h3 className="font-bold">Action Required</h3>
            <div className="text-xs">You have {stats.overdueCount} overdue invoice{stats.overdueCount > 1 ? 's' : ''} requiring attention</div>
          </div>
          <button 
            onClick={() => navigate('/billing/invoices?status=overdue')}
            className="btn btn-sm"
          >
            View Overdue
          </button>
        </div>
      )}
    </div>
  );
};

export default BillingDashboard;
